import { Link, Outlet, useParams } from "react-router-dom";
import { PROBLEM_CODES, api, problemCode, useApiQuery } from "../api";
import type { Company } from "../api";
import { EmptyState, Logo, Skeleton, Tabs } from "../components";
import { useAuthenticatedSession } from "../session";
import { WalletProvider } from "../wallet";
import { AsyncBoundary } from "./AsyncBoundary";
import styles from "./EsnEntrepriseLayout.module.css";
import { ROUTES, esnEntreprisePath } from "./routes";

/**
 * Une entreprise cliente vue par l'ESN : sa fiche et, en onglets, le
 * dashboard, la commande, les missions et l'historique de SON portefeuille.
 *
 * Les pages métier montées dessous sont exactement celles de l'espace
 * entreprise : elles lisent le portefeuille par `useWalletID()` et ne savent
 * pas qui les regarde. Les droits viennent de la session ESN : tout, y compris
 * l'achat, que l'ESN enregistre sans prélèvement pour un règlement par virement.
 */
export function EsnEntrepriseLayout() {
  const { companyId = "" } = useParams();
  const { permissions } = useAuthenticatedSession();
  const entreprise = useApiQuery((o) => api.getCompany(companyId, o), [companyId]);

  if (entreprise.data === null && problemCode(entreprise.error) === PROBLEM_CODES.companyNotFound) {
    return (
      <EmptyState
        icon="🏢"
        title="Entreprise introuvable"
        description="Aucune entreprise ne porte cet identifiant. Elle a peut-être été saisie à la main dans l'adresse."
        action={
          <Link className={styles.lienRetour} to={ROUTES.esnEntreprises}>
            Toutes les entreprises
          </Link>
        }
      />
    );
  }

  return (
    <AsyncBoundary
      state={entreprise}
      errorTitle="Entreprise illisible"
      skeleton={
        <div className={styles.entete} aria-busy="true">
          <Skeleton variant="block" height="4rem" width="4rem" />
          <Skeleton variant="title" width="18rem" />
        </div>
      }
    >
      {(company) => (
        <WalletProvider
          walletId={company.wallet_id}
          companyId={company.id}
          permissions={permissions}
          links={{ commande: esnEntreprisePath(company.id, "commande"), achat: esnEntreprisePath(company.id, "achat") }}
        >
          <Entete company={company} />
          <Outlet context={company} />
        </WalletProvider>
      )}
    </AsyncBoundary>
  );
}

function Entete({ company }: { company: Company }) {
  const onglets = [
    { to: esnEntreprisePath(company.id), label: "Fiche", end: true },
    { to: esnEntreprisePath(company.id, "dashboard"), label: "Dashboard" },
    { to: esnEntreprisePath(company.id, "commande"), label: "Nouveau projet" },
    { to: esnEntreprisePath(company.id, "missions"), label: "Projets" },
    { to: esnEntreprisePath(company.id, "historique"), label: "Historique" },
    { to: esnEntreprisePath(company.id, "achat"), label: "Achat" },
  ];
  return (
    <header className={styles.entete}>
      <Link to={ROUTES.esnEntreprises} className={styles.filAriane}>
        ← Entreprises
      </Link>
      <div className={styles.identite}>
        <Logo src={company.logo_url} name={company.name} size="lg" />
        <div>
          <p className={styles.surtitre}>Entreprise cliente</p>
          <p className={styles.nom}>{company.name}</p>
          <p className={styles.portefeuille}>Portefeuille {company.wallet_id}</p>
        </div>
      </div>
      <Tabs items={onglets} label="Sections de l'entreprise" />
    </header>
  );
}
